import { BaseEdge, EdgeProps, getSmoothStepPath, Position, useInternalNode } from '@xyflow/react';

import type { Model } from '@/explore/types';
import { notNil } from '@/lib/utils';

import type { ModelNodeData } from './ModelNode';

const HeaderHeight = 48;
const PropertyStartY = HeaderHeight + 12;
const propertyLineHeight = 24;

export interface ModelEdgeData extends Record<string, unknown> {
  joinType?: 'direct' | 'stepped';
  joinKeyOnBase?: string;
  joinKeyOnRelated?: string;
  isHovered?: boolean;
}

function calculatePropertyY(model: Model, propertyKey: string, nodeHeight: number): number {
  const propertyIndex = model.properties.findIndex((p) => p.key === propertyKey);
  if (propertyIndex === -1) {
    return nodeHeight / 2;
  }

  return PropertyStartY + propertyIndex * propertyLineHeight + propertyLineHeight / 2;
}

export const ModelEdge = ({
  id,
  source,
  target,
  sourceY,
  targetY,
  data,
  style = {},
}: EdgeProps) => {
  const edgeData = data as ModelEdgeData | undefined;
  const sourceNode = useInternalNode(source);
  const targetNode = useInternalNode(target);

  if (!sourceNode || !targetNode) {
    return null;
  }

  const sourceModel = (sourceNode.data as ModelNodeData | undefined)?.model;
  const targetModel = (targetNode.data as ModelNodeData | undefined)?.model;

  if (!sourceModel || !targetModel) {
    return null;
  }

  // Calculate property Y positions if join keys are provided (relative to node)
  let sourcePropertyY = notNil(sourceNode.measured?.height) ? sourceNode.measured.height / 2 : 0;
  let targetPropertyY = notNil(targetNode.measured?.height) ? targetNode.measured.height / 2 : 0;

  if (edgeData?.joinKeyOnBase !== undefined && notNil(sourceNode.measured?.height)) {
    sourcePropertyY = calculatePropertyY(
      sourceModel,
      edgeData.joinKeyOnBase,
      sourceNode.measured.height,
    );
  }

  if (edgeData?.joinKeyOnRelated !== undefined && notNil(targetNode.measured?.height)) {
    targetPropertyY = calculatePropertyY(
      targetModel,
      edgeData.joinKeyOnRelated,
      targetNode.measured.height,
    );
  }

  // Calculate absolute positions for connection points at property level
  const sourceNodeX = sourceNode.internals.positionAbsolute.x;
  const sourceNodeY = sourceNode.internals.positionAbsolute.y ?? sourceY;
  const sourceWidth = sourceNode.measured?.width ?? 0;

  const targetNodeX = targetNode.internals.positionAbsolute.x;
  const targetNodeY = targetNode.internals.positionAbsolute.y ?? targetY;
  const [edgePath] = getSmoothStepPath({
    sourcePosition: Position.Right,
    sourceX: sourceNodeX + sourceWidth,
    sourceY: sourceNodeY + sourcePropertyY + 2, // Center the edge on the property
    targetX: targetNodeX,
    targetY: targetNodeY + targetPropertyY + 2, // Center the edge on the property
    targetPosition: Position.Left,
    borderRadius: 32,
  });

  const isHovered = edgeData?.isHovered === true;
  const strokeColor = isHovered ? '#8B5CF6' : '#44485c';
  const strokeWidth = isHovered ? 2 : 1;

  const marker = isHovered
    ? 'url(#edge-attachment-circle-hovered)'
    : 'url(#edge-attachment-circle)';

  return (
    <>
      <svg style={{ position: 'absolute', top: 0, left: 0 }}>
        <defs>
          <marker
            id="edge-attachment-circle"
            viewBox="0 0 20 20"
            markerHeight={10}
            markerWidth={10}
            refX={10}
            refY={10}>
            <circle r={10} cx={10} cy={10} fill="#44485c" />
          </marker>
          <marker
            id="edge-attachment-circle-hovered"
            viewBox="0 0 20 20"
            markerHeight={5}
            markerWidth={5}
            refX={10}
            refY={10}>
            <circle r={10} cx={10} cy={10} fill="#8B5CF6" />
          </marker>
        </defs>
      </svg>

      <BaseEdge
        id={id}
        path={edgePath}
        markerStart={marker}
        markerEnd={marker}
        style={{
          ...style,
          stroke: strokeColor,
          strokeWidth,
        }}
      />
    </>
  );
};
